/**
 * Brand Bible page draft model: the local draft shape, its mapping to and from the
 * persisted BrandBible, the save payload builders and the section completion checks.
 *
 * Moved out of BrandBiblePage.tsx (3,000-line rule) with no behaviour change; the page
 * re-exports the symbols its tests and sections import from it.
 */
import type { BrandBible, BuyerPersona } from '../../types/tenant';
import type { ColorValue } from '../../components/brand-bible';
import type { SectionId } from '@/lib/bbSectionMap';

// Swatch values the user picks from; the chosen hex is persisted on the brand
// bible and re-used outside this app (exports, generated content). These are data,
// not app chrome, so they stay literal hexes rather than theme tokens.
export const PERSONA_COLORS = ['#4CC9F0', '#0A76A8', '#111111', '#FBBF24', '#16A34A', '#6B7280', '#7C3AED', '#EC4899'];
export const SUGG_WORDS_AVOID = ['guys', 'cheap', 'best in town', '#1', 'hack'];
export const SUGG_SERVICES_NOT = ['Septic systems', 'Commercial only', 'New construction', 'Well pumps'];

// ---------------------------------------------------------------------------
// Local draft model — superset of persisted BrandBible plus design-only fields.
// Persisted fields go through `useUpdateBrandBible`; local-only fields are kept
// in component state until the backend schema accepts them.
// ---------------------------------------------------------------------------

// A type alias, not an interface: an alias has an implicit index signature, so it is assignable to the
// tenant type's `{ [key: string]: string | undefined }` social map.
export type SocialLinks = {
  facebook: string;
  instagram: string;
  linkedin: string;
  twitter: string;
  youtube: string;
};

// exported for SectionAudience (restored from fold-in park 55822ae6f, recover/bb-e-foldin)
export interface BrandBibleDraft {
  // persisted (backend-accepted)
  business_name: string;
  description: string;
  service_locations: string[];
  services_offered: string[];
  ideal_client: string;
  marketing_focus: string;
  value_proposition: string;
  // persisted via blocklist hook (separate table)
  competitors: string[];
  // partially persisted via existing GET response, but PUT schema doesn't accept these.
  // Kept in local state — see file header.
  website: string;
  phone: string;
  email: string;
  address: string;
  company_mission: string;
  tone_of_writing: string;
  call_to_action: string;
  customer_pain_points: string;
  website_audience: string;
  seasonality: string;
  primary_colors: ColorValue[];
  secondary_colors: ColorValue[];
  typography: string;
  visual_identity: string;
  words_avoid: string[];
  services_not_offered: string[];
  regulatory_notes: string;
  credentials_licenses: string;
  case_studies: string;
  social: SocialLinks;
  buyer_personas: BuyerPersona[];
  author_attribution: BrandBible['author_attribution'];
}

export const EMPTY_DRAFT: BrandBibleDraft = {
  business_name: '',
  description: '',
  service_locations: [],
  services_offered: [],
  ideal_client: '',
  marketing_focus: '',
  value_proposition: '',
  competitors: [],
  website: '',
  phone: '',
  email: '',
  address: '',
  company_mission: '',
  tone_of_writing: '',
  call_to_action: '',
  customer_pain_points: '',
  website_audience: '',
  seasonality: '',
  primary_colors: [],
  secondary_colors: [],
  typography: '',
  visual_identity: '',
  words_avoid: [],
  services_not_offered: [],
  regulatory_notes: '',
  credentials_licenses: '',
  case_studies: '',
  social: { facebook: '', instagram: '', linkedin: '', twitter: '', youtube: '' },
  buyer_personas: [],
  author_attribution: null,
};

// Hex string ↔ ColorValue[] conversion. The DB stores `primary_colors` as a single
// string of comma-separated hex codes (legacy schema); design uses {hex,label}[].
export const parseColors = (raw: string | undefined): ColorValue[] => {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((hex) => ({ hex: hex.startsWith('#') ? hex : `#${hex}`, label: '' }));
};

export const stringifyColors = (colors: ColorValue[]): string | undefined => {
  const value = colors.map((c) => c.hex.trim()).filter(Boolean).join(',');
  return value || undefined;
};

export const normalizeWebsiteUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// The §7.1 projection's `service_locations` flat column is built from
// bb_locations with `fallback_field: location_key`, so the tenant's primary
// row (location_key='primary', usually no `metro`) emits the literal string
// "primary" — an internal sentinel, never a city. Strip it here at the row
// source so it can never render as a chip and can never be fed back into a
// write (runLocationsDiffSync would otherwise upsert a bogus metro='primary'
// row). Exact, trimmed, case-insensitive match only — a real city that merely
// CONTAINS the word (e.g. "Primary Springs") must survive.
export const stripPrimaryLocationSentinel = (locations: string[]): string[] =>
  locations.filter((loc) => loc.trim().toLowerCase() !== 'primary');

export const fromBrandBible = (bb: BrandBible | null | undefined, blocklist: string[]): BrandBibleDraft => {
  if (!bb) return { ...EMPTY_DRAFT, competitors: blocklist };
  const social = (bb.social_media_links ?? {}) as Partial<SocialLinks>;
  const restrictions = bb.brand_restrictions ?? null;
  const themeConfig = bb.theme_config ?? null;
  const typography = themeConfig?.typography;
  return {
    business_name: bb.business_name ?? '',
    description: bb.description ?? '',
    service_locations: stripPrimaryLocationSentinel(bb.service_locations ?? []),
    services_offered: bb.services_offered ?? [],
    ideal_client: bb.ideal_client ?? '',
    marketing_focus: bb.marketing_focus ?? '',
    value_proposition: bb.value_proposition ?? '',
    // Competitors come from the blocklist table, not bb.competitors (legacy field).
    competitors: blocklist,
    website: bb.contact_info?.website ?? bb.website ?? '',
    phone: bb.contact_info?.phone ?? '',
    email: bb.contact_info?.email ?? '',
    address: bb.contact_info?.address ?? '',
    company_mission: bb.company_mission ?? '',
    tone_of_writing: bb.tone_of_writing ?? '',
    call_to_action: bb.call_to_action ?? '',
    customer_pain_points: bb.customer_pain_points ?? '',
    website_audience: bb.website_audience ?? '',
    seasonality: bb.seasonality ?? '',
    primary_colors: parseColors(bb.primary_colors ?? themeConfig?.colors?.primary),
    secondary_colors: parseColors(bb.secondary_colors ?? themeConfig?.colors?.secondary),
    typography: bb.typography ?? (typeof typography === 'string' ? typography : typography?.fontFamily) ?? '',
    visual_identity: bb.visual_identity ?? '',
    words_avoid: restrictions?.words_avoid ?? restrictions?.words_to_avoid ?? [],
    services_not_offered: restrictions?.services_not_offered ?? [],
    regulatory_notes: restrictions?.regulatory_notes ?? '',
    credentials_licenses: bb.credentials_licenses ?? '',
    case_studies: bb.case_studies ?? '',
    social: {
      facebook: social.facebook ?? '',
      instagram: social.instagram ?? '',
      linkedin: social.linkedin ?? '',
      twitter: social.twitter ?? '',
      youtube: social.youtube ?? '',
    },
    buyer_personas: bb.buyer_personas ?? [],
    author_attribution: bb.author_attribution ?? null,
  };
};

export const buildBrandBiblePayload = (d: BrandBibleDraft, website: string): BrandBible => ({
  website,
  business_name: d.business_name,
  description: d.description,
  services_offered: d.services_offered,
  service_locations: d.service_locations,
  ideal_client: d.ideal_client,
  marketing_focus: d.marketing_focus,
  value_proposition: d.value_proposition,
  competitors: d.competitors,
  company_mission: d.company_mission,
  tone_of_writing: d.tone_of_writing,
  call_to_action: d.call_to_action,
  customer_pain_points: d.customer_pain_points,
  website_audience: d.website_audience,
  seasonality: d.seasonality,
  visual_identity: d.visual_identity,
  credentials_licenses: d.credentials_licenses,
  case_studies: d.case_studies,
  contact_info: {
    website: d.website,
    phone: d.phone,
    email: d.email,
    address: d.address,
  },
  social_media_links: d.social,
  brand_restrictions: {
    words_avoid: d.words_avoid,
    words_to_avoid: d.words_avoid,
    services_not_offered: d.services_not_offered,
    regulatory_notes: d.regulatory_notes,
  },
  buyer_personas: d.buyer_personas,
  author_attribution: d.author_attribution,
  theme_config: {
    colors: {
      primary: stringifyColors(d.primary_colors),
      secondary: stringifyColors(d.secondary_colors),
    },
    typography: {
      fontFamily: d.typography,
    },
  },
});

/**
 * §7.6: flat brand_bible columns backed by this draft that are genuinely
 * `legacy_only` in projection_map.json — i.e. the server's
 * PATCH /legacy-fields will accept them. Deliberately NOT the full ~15-field
 * list from the §7.5 report: ideal_client/tone_of_writing/seasonality are
 * `context` (bb_chunks — a real v2 home, just not wired to a UI control yet)
 * and credentials_licenses/case_studies are `claim` (bb_credentials/
 * bb_case_studies) — the server 422s all five here, on purpose, because they
 * already have a v2 home. Routing those needs bb_ wiring, not this endpoint;
 * they remain silently unsaved for v2-active tenants until that follow-up.
 */
export const LEGACY_ONLY_SCALAR_KEYS = [
  'marketing_focus',
  'value_proposition',
  'company_mission',
  'call_to_action',
  'customer_pain_points',
  'website_audience',
  'visual_identity',
  'buyer_personas',
  'author_attribution',
] as const;

export const buildLegacyOnlyPayload = (d: BrandBibleDraft, website: string): Record<string, unknown> => {
  const full = buildBrandBiblePayload(d, website);
  const out: Record<string, unknown> = {};
  for (const key of LEGACY_ONLY_SCALAR_KEYS) {
    out[key] = full[key];
  }
  // Composite — multiple draft fields fold into one flat column; built from
  // `full` so the composition logic lives in exactly one place.
  out.brand_restrictions = full.brand_restrictions;
  // theme_config is deliberately NOT sent: projection_map.json classes it
  // `context` (composed from bb_visual_identity), so the server 422s the
  // WHOLE batch when it appears — which made every flat-field save fail for
  // every v2-active tenant. Its v2 write home is bb_visual_identity, not
  // this endpoint.
  return out;
};

// ---------------------------------------------------------------------------
// Section completion checks (drive progress ring + sub-nav checkmarks)
// ---------------------------------------------------------------------------

export const isFilled = (v: unknown): boolean =>
  Array.isArray(v) ? v.length > 0 : typeof v === 'string' ? v.trim().length > 0 : Boolean(v);

/** Minimal shape sectionStatus/the §C nudge need from a v2 `services` row — never the full BbEntry. */
export type PriceGapServiceLike = { name?: string | null; price_policy?: string | null };
/** Minimal shape from a v2 `offers` row. */
export type OfferKindLike = { kind?: string | null };

export interface PriceGapSummary {
  /** How many chips in services_offered — the count the "N of M" nudge is out of. */
  total: number;
  /** Chips whose matching v2 services row has price_policy set. */
  withPolicy: number;
  /** total - withPolicy — the count that still block launch (gap 4a). */
  missing: number;
}

/**
 * §C price-policy nudge (gap 4a) + the C1 rail-truth check (gap 4b) share this:
 * match each `services_offered` chip to its v2 `services` row by name (same
 * natural-key lookup as `findServiceEntry`) and count how many are missing
 * `price_policy`. Kept as one function so the nudge line and the rail dot can
 * never silently disagree on the count.
 */
export const computePriceGapSummary = (
  servicesOffered: string[],
  v2Services: PriceGapServiceLike[],
): PriceGapSummary => {
  const byName = new Map(
    v2Services.map((e) => [(e.name ?? '').trim().toLowerCase(), e]),
  );
  const total = servicesOffered.length;
  const withPolicy = servicesOffered.filter((name) =>
    isFilled(byName.get(name.trim().toLowerCase())?.price_policy),
  ).length;
  return { total, withPolicy, missing: total - withPolicy };
};

/** §C offers facts (contract: guarantee/dispatch fee/financing) — the other 3 of the C1 4-item definition. */
export const CORE_OFFER_KINDS = ['guarantee', 'dispatch_fee', 'financing'] as const;
export const hasCoreOffers = (v2Offers: OfferKindLike[]): boolean => {
  const kinds = new Set(v2Offers.map((o) => o.kind));
  return CORE_OFFER_KINDS.every((k) => kinds.has(k));
};

/**
 * v2 context sectionStatus needs for the §C completeness check. Optional —
 * when a caller has no v2 data on hand it's omitted and §C falls back to the
 * pre-v2 flat-field check (see 'service' case below); passing it (even with
 * empty arrays, meaning "no v2 rows yet") makes the check strict.
 */
export interface SectionStatusV2Context {
  services?: PriceGapServiceLike[];
  offers?: OfferKindLike[];
}

export const sectionStatus = (
  s: SectionId,
  d: BrandBibleDraft,
  v2?: SectionStatusV2Context,
): boolean => {
  switch (s) {
    case 'essentials':
      return ['business_name', 'website', 'description', 'phone'].every((k) =>
        isFilled(d[k as keyof BrandBibleDraft]),
      );
    case 'service': {
      // Legacy flat-row truth: locations + at least one service picked.
      if (!(isFilled(d.service_locations) && isFilled(d.services_offered))) return false;
      // No v2 context on hand (e.g. a caller with no bb_services/bb_offers
      // access) — the flat-row check above is all we can measure.
      if (!v2) return true;
      // C1's 4-item definition: price_policy on every service, plus
      // guarantee + dispatch fee + financing facts recorded. Previously this
      // branch never looked past the two flat-row fields, so the rail could
      // read "done" while every service still lacked a price policy.
      const gap = computePriceGapSummary(d.services_offered, v2.services ?? []);
      return gap.missing === 0 && hasCoreOffers(v2.offers ?? []);
    }
    case 'voice':
      return (
        isFilled(d.company_mission) && isFilled(d.value_proposition) && isFilled(d.tone_of_writing)
      );
    case 'audience':
      return isFilled(d.ideal_client) && isFilled(d.customer_pain_points);
    case 'visual':
      return d.primary_colors.length > 0;
    case 'guardrails':
      return isFilled(d.words_avoid) || isFilled(d.services_not_offered) || isFilled(d.regulatory_notes);
    case 'proof':
      return isFilled(d.credentials_licenses) || isFilled(d.case_studies) || Object.values(d.social).some(isFilled);
    default:
      return false;
  }
};

/** Sections the legacy flat-row checks can actually measure (pre-v2 tenants). */
export const LEGACY_SCORED = new Set<SectionId>([
  'essentials',
  'service',
  'voice',
  'audience',
  'visual',
  'guardrails',
  'proof',
]);

export const fmtSavedAt = (d: Date | null): string => {
  if (!d) return '—';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 5) return 'just now';
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const normalizeWebsiteInput = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return trimmed.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
};
